<template>
  <div class="objects-select-input clearfix">
    <div class="form-group row">
      <label :class="options.fullWidth ? 'col-sm-12' : 'col-sm-4 col-form-label'">
        {{ options.label }}
        <span v-if="options.required" class="required">*</span>
      </label>
      <div :class="options.fullWidth ? 'col-sm-12' : 'col-sm-8'">
        <select class="form-control" multiple ref="dropdown"
          @change="validate()"
          :name="options.name+'[]'">
          <option v-for="object in options.objects" :value="object.uuid">{{ object[options.objectName] }}</option>
        </select>
        <span :class="validationClass" v-show="validationMessage">
          {{ validationMessage }}
        </span>
        <button class="btn btn-sm btn-primary mt-2" type="button" @click="selectAll()">
          <span class="far" :class="{ 'fa-check-square': !allSelected, 'fa-square': allSelected }"></span> {{ !allSelected ? "Select" : "Unselect" }} All
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Choices from 'choices.js'
import {
  validationClass,
  validationIcon,
  validationMessage,
  randomLabel
} from '@/util/helpers'

export default {
  name: 'ObjectsSelectInput',
  props: {
    value: {
      type: [Array, String]
    },
    options: {
      type: Object,
      default () {
        return {
          label: 'Text',
          name: 'objectsSelect',
          allowNew: false,
          objectName: 'name',
          value: [],
          objects: []
        }
      }
    }
  },
  computed: {
    validationClass,
    validationIcon,
    validationMessage,
    invalidMessage () {
      return this.options.label + ' is required'
    },
    allSelected () {
      return this.value.length === this.options.objects.length
    }
  },
  data () {
    return {
      isDirty: false,
      isValid: false,
      choices: null
    }
  },
  mounted () {
    let options = this.options
    function callbackOnCreateTemplates (template) {
      let classNames = this.config.classNames
      return {
        item: (data) => {
          let object = options.objects.find(el => el.uuid === data.value)
          let label = object[options.objectName]
          let closeButton = `
          <button class="${classNames.button}" data-button>Remove item</button>
          `
          if (data.id && object) {
            if (object[options.iconName]) {
              let prefix = object[options.iconName] === 'fa-bluetooth' ? 'fab' : 'fa'
              label = `<span class="${prefix} fa-fw ${object[options.iconName]}"></span> ${label}`
            }
          }
          if (data.title) {
            label = `${label}  ${data.title}`
          }
          if (options.labelName) {
            let labelText = object[options.labelName]
            let classNames = randomLabel(labelText)
            let className = 'label-default'
            for (let labelClass in classNames) {
              if (classNames[labelClass]) className = labelClass
            }
            label = `${label}&nbsp;&nbsp;<span class="label ${className}">${labelText}</span>`
          }
          if (this.config.removeItemButton) {
            label = `${label} ${closeButton}`
          }
          return template(`
          <div class="${classNames.item} ${data.highlighted ? classNames.highlightedState : classNames.itemSelectable}"  data-item data-id="${data.id}" data-value="${object.uuid}" ${data.active ? 'aria-selected="true"' : ''} ${data.disabled ? 'aria-disabled="true"' : ''}>${label}</div>
          `)
        },
        choice: (data) => {
          let object = options.objects.find(el => el.uuid === data.value)
          let label = object[options.objectName]
          if (data.id && object) {
            if (object[options.iconName]) {
              let prefix = object[options.iconName] === 'fa-bluetooth' ? 'fab' : 'fa'
              label = `<span class="${prefix} fa-fw ${object[options.iconName]}"></span> ${label}`
            }
          }
          let coloredLabel = ''
          if (options.labelName) {
            let labelText = object[options.labelName]
            let classNames = randomLabel(labelText)
            let className = 'label-default'
            for (let labelClass in classNames) {
              if (classNames[labelClass]) className = labelClass
            }
            coloredLabel = `&nbsp;&nbsp;<span class="label ${className}">${labelText}</span>`
          }
          return template(`
          <div class="${classNames.item} ${classNames.itemChoice} ${data.disabled ? classNames.itemDisabled : classNames.itemSelectable} ${data.disabled ? classNames.itemDisabled : classNames.itemSelectable}" data-select-text="${this.config.itemSelectText}" data-choice ${data.disabled ? 'data-choice-disabled aria-disabled="true"' : 'data-choice-selectable'} data-id="${data.id}" data-value="${object.uuid}" ${data.groupId > 0 ? 'role="treeitem"' : 'role="option"'}>${label}${coloredLabel}</div>
          `)
        }
      }
    }
    let $vm = this
    this.choices = new Choices(this.$refs.dropdown, {
      callbackOnCreateTemplates,
      itemSelectText: '',
      removeItemButton: true,
      searchFields: ['label'],
      fuseOptions: {
        threshold: 0.1,
        matchAllTokens: true,
        tokenize: true,
        maxPatternLength: 128
      },
      callbackOnInit: function () {
        if (!$vm.value) {
          let el = this.store.getItemsFilteredByActive().pop()
          if (el) this.removeItemsByValue(el.value)
        } else {
          this.setValueByChoice($vm.value)
        }
      }
    })
  },
  watch: {
    'options.objects': function (oldVal, newVal) {
      this.updateChoices()
    }
  },
  methods: {
    selectAll () {
      if (this.allSelected) {
        this.choices.removeHighlightedItems()
        this.choices.removeActiveItems()
      } else {
        this.choices.setValueByChoice(this.options.objects.map(el => el.uuid))
      }
      this.validate()
    },
    async updateChoices () {
      if (this.choices) {
        // updates the choices input with new values
        this.choices.clearInput()
        this.choices.config.searchPlaceholderValue = 'Select a ' + this.options.label
        this.choices.config.placeholderValue = 'Select a ' + this.options.label
        if (this.choices.containerInner.children[1].children[0]) {
          this.choices.containerInner.children[1].children[0].innerHTML = ''
        }
        this.choices.containerOuter.children[1].children[0].placeholder = 'Select a ' + this.options.label
        this.choices.setChoices(this.options.objects, 'uuid', this.options.objectName, true)
        await this.$nextTick()
        if (this.value) {
          // let val = this.choices.getValue(true)
          this.choices.removeHighlightedItems()
          this.choices.removeActiveItems()
          this.choices.setValueByChoice(this.value)
        }
      }
    },
    async validate () {
      await this.$nextTick()
      let val = this.choices.getValue(true)
      this.$emit('input', val)
      if (val.length > 0) {
        this.isDirty = true
        if (this.options.required) {
          this.isValid = val ? val.length > 0 : true
        } else {
          this.isValid = true
        }
      } else if (this.options.required) {
        this.isDirty = true
        this.isValid = false
      } else {
        this.isValid = true
      }
    },
    runWatcher (val, name) {
      switch (name) {
        case 'allCars':
        case 'council':
        case 'contract':
          if (this.options.name === 'cars') {
            if (val) {
              this.$el.style.display = 'none'
            } else {
              this.$el.style.display = 'block'
            }
          }
          break
        case 'allMembers':
          if (this.options.name === 'postcodes') {
            if (val) {
              this.$el.style.display = 'none'
            } else {
              this.$el.style.display = 'block'
            }
          }
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/inputs/ObjectsSelectInput.vue